import { usePropositionForTemplate } from "../api/queries/propositions.queries";
import { addStaticData, replacePlaceholders } from "../utils/template.utils";

interface UsePropositionTemplateDataProps {
	propositionId: number;
	enabled?: boolean;
}

export const usePropositionTemplateData = ({
	propositionId,
	enabled = true,
}: UsePropositionTemplateDataProps) => {
	const { data, isLoading, error } = usePropositionForTemplate(propositionId, {
		enabled,
	});

	const processTemplate = (template: string): string => {
		if (!data) return template;

		const enrichedData = addStaticData(data);

		return replacePlaceholders(template, enrichedData);
	};

	const processEmailTemplate = (subject: string, htmlContent: string) => {
		if (!data) return { subject, htmlContent };

		const enrichedData = addStaticData(data);

		const processedSubject = replacePlaceholders(subject, enrichedData);
		const processedHtmlContent = replacePlaceholders(htmlContent, enrichedData);

		return {
			subject: processedSubject,
			htmlContent: processedHtmlContent,
		};
	};

	const getDebugInfo = () => {
		if (!data) return null;

		const enrichedData = addStaticData(data);

		const generatePlaceholders = (
			obj: Record<string, unknown>,
			prefix = "",
		): string[] => {
			const placeholders: string[] = [];

			for (const [key, value] of Object.entries(obj)) {
				const currentPath = prefix ? `${prefix}.${key}` : key;

				if (
					value !== null &&
					typeof value === "object" &&
					!Array.isArray(value)
				) {
					placeholders.push(
						...generatePlaceholders(
							value as Record<string, unknown>,
							currentPath,
						),
					);
				} else {
					placeholders.push(`{{${currentPath}}}`);
				}
			}

			return placeholders;
		};

		const availablePlaceholders = generatePlaceholders(enrichedData);

		return {
			rawData: data,
			enrichedData,
			availablePlaceholders,
			dataStructure: JSON.stringify(enrichedData, null, 2),
		};
	};

	return {
		data,
		isLoading,
		error,
		processTemplate,
		processEmailTemplate,
		getDebugInfo,
	};
};
